import { FC, useEffect, useState, useMemo } from "react";
import { CommonSearch } from "@ui/common-search";
import { useSearch } from "@hooks/useSearch";
import { SELECTS_ID } from "@store/company/procedure-instrunctions-store";
import {
  GetGeneralMedicalExamControlsParams,
  MedicalExamControlsBody,
  MedicalExamControlsT,
} from "@interfaces/medical-examination";
import styles from "./general-medical-exam-controls.module.scss";
import {
  useDownloadGeneralMedicalExamControlsMutation,
  useGeneralMedicalExamControlsQuery,
  useMedicalExaminationFactorsQuery,
  useMedicalExaminationQuery,
  useUpdateMedicalExamControlsMutation,
} from "@lib/medical-examination";
import { MedicalExamControlsTable } from "../medical-exam-controls/medical-exam-controls-table";
import { useSorting } from "@hooks/useSorting";
import { Pagination } from "@components/pagination";
import {
  MedicalExamControlsFilters,
  SelectList,
} from "../medical-exam-controls/medical-exam-controls-filters";
import { useDepartmentsGetAllQuery } from "@lib/departments-get-all/departments-get-all";
import { convertToSelectList } from "@utils/convertToSelectList";
import { usePositionsGetAllQuery } from "@lib/positions-get-all/positions-get-all";
import { useEmployeesGetAllQuery } from "@lib/employees-get-all/employees-get-all";
import { useCompaniesQuery } from "@lib/company";
import { useQueryClient } from "react-query";
import { medicalExaminationQueryKeys } from "@lib/medical-examination/queryKeys";
import { FileLoadSVG } from "@components/svgs";
import { downloadBlob } from "@utils/downloadBlob";
import toast from "react-hot-toast";
import { getProcedureControlParamsArray } from "@utils/getProcedureControlParamsArray";

const LIMIT = 20;
const getOffset = (page: number) => LIMIT * (page - 1);

const sortingKeys = [
  "lastDateOrder",
  "companyNameOrder",
  "departmentTitleOrder",
  "positionTitleOrder",
  "employeeNameOrder",
  "factorTitleOrder",
  "nextDateOrder",
  "daysBeforeNextDateOrder",
];

export const GeneralMedicalExamControls: FC = () => {
  const queryClient = useQueryClient();
  const updateControlsMutation = useUpdateMedicalExamControlsMutation();
  const downloadControlsMutation =
    useDownloadGeneralMedicalExamControlsMutation();

  const [page, setPage] = useState(1);
  const search = useSearch();
  const [selectedCategory, setSelectedCategory] = useState<SelectList[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<SelectList[]>(
    []
  );
  const [selectedPositions, setSelectedPositions] = useState<SelectList[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<SelectList[]>([]);
  const [selectedFactors, setSelectedFactors] = useState<SelectList[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<SelectList[]>([]);
  const [startDate, setStartDate] = useState<number[]>();
  const [deadline, setDeadline] = useState<number[]>();

  const { data: categories } = useMedicalExaminationQuery();
  const { data: departments } = useDepartmentsGetAllQuery(
    { companies: selectedCompanies.map((c) => c.id) },
    true
  );
  const { data: positions } = usePositionsGetAllQuery(
    {
      companies: selectedCompanies.map((c) => c.id),
      departments: selectedDepartments.map((d) => d.title),
    },
    true
  );
  const { data: factorsData } = useMedicalExaminationFactorsQuery();
  const { data: employees } = useEmployeesGetAllQuery(
    {
      companies: selectedCompanies.map((c) => c.id),
      departments: selectedDepartments.map((d) => d.title),
      positions: selectedPositions.map((p) => p.title),
    },
    true
  );

  useEffect(() => {
    if (categories) {
      setSelectedCategory(
        categories?.map((e) => ({ id: e.id, title: e.title }) as SelectList)
      );
    }
  }, [categories]);

  const { companies } = useCompaniesQuery();
  const categoriesList = categories?.filter(
    (category) => typeof category.id !== "undefined"
  );
  const categoriesListData = categoriesList?.map(
    (e) => ({ id: e.id, title: e.title }) as SelectList
  );

  const departmentsListData = convertToSelectList(
    departments ?? [],
    (item) => item.id,
    (item) => item.title ?? String(item.id)
  );
  const departmentsList = useMemo(() => {
    return [
      {
        title: "Все подразделения",
        id: SELECTS_ID,
      },
      ...(departmentsListData?.map((item) => ({
        title: item.title,
        id: item.id,
      })) ?? []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentsListData]);

  const positionsListData = convertToSelectList(
    positions ?? [],
    (item) => item.id,
    (item) => item.title ?? String(item.id),
    (position) => position.parentId === null
  );
  const positionsList = useMemo(() => {
    return [
      {
        title: "Все подразделения",
        id: SELECTS_ID,
      },
      ...(positionsListData?.map((item) => ({
        title: item.title,
        id: item.id,
      })) ?? []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionsListData]);

  const employeesListData = convertToSelectList(
    employees ?? [],
    (item) => item.id,
    (item) => item.name ?? String(item.id)
  );
  const employeesList = useMemo(() => {
    return [
      {
        title: "Все сотрудники",
        id: SELECTS_ID,
      },
      ...(employeesListData?.map((item) => ({
        title: item.title,
        id: item.id,
      })) ?? []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeesListData]);

  const factors = factorsData?.map((item) => item.factors).flat();
  const factorsListData = convertToSelectList(
    factors ?? [],
    (item) => item.id,
    (item) => item.title
  );
  const factorsList = useMemo(() => {
    return [
      {
        title: "Все факторы",
        id: SELECTS_ID,
      },
      ...(factorsListData?.map((item) => ({
        title: item.title,
        id: item.id,
      })) ?? []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factorsListData]);

  const companiesListData = convertToSelectList(
    companies ?? [],
    (item) => item.id,
    (item) => item.name
  );
  const companiesList = useMemo(() => {
    return [
      {
        title: "Все компании",
        id: SELECTS_ID,
      },
      ...(companiesListData?.map((item) => ({
        title: item.title,
        id: item.id,
      })) ?? []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companiesListData]);

  const sorting = useSorting(sortingKeys, "ASC");

  const params: GetGeneralMedicalExamControlsParams = {
    offset: getOffset(page),
    limit: LIMIT,
    categories: selectedCategory ? selectedCategory.map((e) => e.id) : [],
    departments: getProcedureControlParamsArray(
      selectedDepartments,
      departments?.length ?? 0,
      true
    ),
    positions: getProcedureControlParamsArray(
      selectedPositions,
      positions?.length ?? 0,
      true
    ),
    employees: getProcedureControlParamsArray(
      selectedEmployees,
      employees?.length ?? 0,
      true
    ),
    factors: getProcedureControlParamsArray(
      selectedFactors,
      factors?.length ?? 0,
      false
    ),
    companies: getProcedureControlParamsArray(
      selectedCompanies,
      companies?.length ?? 0,
      false
    ),
    date: startDate,
    deadline,
    searchString: search.debounced,
    ...sorting.getActiveSorting(),
  };

  const { data, isLoading } = useGeneralMedicalExamControlsQuery(params);

  let dataView: any = [];
  data?.forEach((e) => {
    if (e.body.length) dataView = dataView.concat(e.body);
  });

  const hasNextPage = (dataView.length ?? 0) >= LIMIT;

  const handleUpdateLastDate = (
    row: MedicalExamControlsBody,
    lastDate: string | null
  ) => {
    updateControlsMutation.mutate(
      {
        companyId: row.companyId,
        id: row.id,
        lastDate,
      },
      {
        onSuccess: (result) => {
          toast.success("Данные обновлены");
          queryClient.setQueryData<MedicalExamControlsT[] | null>(
            medicalExaminationQueryKeys.generalControls(params),
            (state) => {
              if (!state) return null;

              return state.map((table) => {
                table.body = table.body.map((_row) =>
                  _row.id === row.id ? { ..._row, ...result } : _row
                );
                return table;
              });
            }
          );
        },
      }
    );
  };

  const getExcelFileName = () => {
    if (typeof selectedCategory === "undefined") return;

    let filename = "";
    if (selectedCategory) {
      filename = `Контроль медицинских осмотров [${
        selectedCategory.length > 1
          ? selectedCategory.map((e) => e.title).join(", ")
          : selectedCategory[0].title
      }].xlsx`;
    } else {
      filename = `Контроль медицинских осмотров.xlsx`;
    }

    return filename;
  };

  const handleDownloadTable = () => {
    downloadControlsMutation.mutate(params, {
      onSuccess: (blob) => {
        const filename = getExcelFileName();
        if (!filename) return;
        downloadBlob(blob, filename);
      },
    });
  };

  return (
    <div className={styles.controls}>
      <CommonSearch
        className="ml-auto"
        onChange={() => {}}
        setSearch={search.setValue}
      />
      <div className={styles.controls__content}>
        <div className={styles.controls__downloadActions}>
          <button
            className={styles.controls__downloadBtn}
            onClick={handleDownloadTable}
          >
            <FileLoadSVG />
            Скачать текущую таблицу
          </button>
        </div>
        <MedicalExamControlsFilters
          departments={departmentsList}
          positions={positionsList}
          employees={employeesList}
          factors={factorsList}
          companies={companiesList}
          categories={categoriesListData}
          selectedDepartments={selectedDepartments}
          selectedPositions={selectedPositions}
          selectedEmployees={selectedEmployees}
          selectedFactors={selectedFactors}
          selectedCompanies={selectedCompanies}
          selectedCategory={selectedCategory}
          onDepartmentsChange={(values) => {
            setSelectedDepartments(values);
            setSelectedPositions([]);
            setSelectedEmployees([]);
          }}
          onPositionChange={(values) => {
            setSelectedPositions(values);
            setSelectedEmployees([]);
          }}
          onEmployeeChange={setSelectedEmployees}
          onFactorChange={setSelectedFactors}
          onStartDateChange={setStartDate}
          onDeadlineChange={setDeadline}
          onCompaniesChange={(values) => {
            setSelectedCompanies(values);
            setSelectedDepartments([]);
            setSelectedPositions([]);
            setSelectedEmployees([]);
          }}
          onCategoriesChange={setSelectedCategory}
        />
        <div>
          {/* Этот участок могут вернуть */}
          {/* <Text size={30} color="green" weight="bold" mt={48} mb={24}>
            { selectedCategory?.map(e => e.title).join(", ") }
          </Text> */}
          <MedicalExamControlsTable
            data={dataView ?? []}
            isLoading={isLoading}
            sorting={sorting}
            offset={params.offset}
            withCompany
            onUpdateLastDate={handleUpdateLastDate}
          />
          <div className={styles.controls__pagination}>
            <Pagination
              value={page}
              onChange={setPage}
              total={hasNextPage ? page + 1 : page}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
